import React, { useEffect, useState, useCallback } from 'react';
import { useSearchParams, useParams, useNavigate } from 'react-router-dom';

// API/Components
import axiosInstance from '../components/axios';
import DataViewSwitch from '../components/data_view_switch';
import UserPrefsManager from '../components/user_prefs';
import PasswordResetDialog from '../components/password_reset';
import TempPassDialog from '../components/temp_password';
import MFASetupDialog from '../components/mfa_setup';
// import MFABackupCodesDialog from '../components/mfa_regen';
import DeactivateMFADialog from '../components/mfa_prompt';
import { formatDateTime } from '../components/enums';
import { isValidEmail, isValidName, isValidTimeZone } from '../components/validation';
import Toast from '../components/toast';
import { TIME_ZONES } from '../components/time_zones';

// Material UI
import Avatar from '@mui/material/Avatar';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Grid from '@mui/material/Grid';
import MenuItem from '@mui/material/MenuItem';
import Paper from '@mui/material/Paper';
import Select from '@mui/material/Select';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';

// Material UI Icons
import PersonIcon from '@mui/icons-material/Person';
import LockIcon from '@mui/icons-material/Lock';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import SettingsIcon from '@mui/icons-material/Settings';
import HistoryIcon from '@mui/icons-material/History';

// Style constants for consistent theming
const STYLES = {
	// Colors
	colors: {
		brandRed: '#B1282E',
		brandRedDark: '#8B1F24',
		textPrimary: '#1a2332',
		textSecondary: '#6c757d',
		textTertiary: '#495057',
		textDark: '#0F1820',
		backgroundPage: '#ffffff',
		backgroundWhite: '#ffffff',
		backgroundLight: '#f8f9fa',
		backgroundSection: '#ffffff',
		borderLight: '#e0e0e0',
		avatarGreen: '#2d6a4f',
		avatarGreenDark: '#1b4332',
	},
	
	// Spacing
	spacing: {
		sectionPadding: 3,
		sectionMarginBottom: 2.5,
		containerMaxWidth: '1200px',
	},
	
	// Shadows
	shadows: {
		card: '0 2px 8px rgba(0,0,0,0.1)',
		cardHover: '0 4px 12px rgba(0,0,0,0.15)',
		avatar: '0 4px 12px rgba(0,0,0,0.15)',
		avatarCard: '0 4px 12px rgba(0,0,0,0.12)',
		avatarCardHover: '0 6px 20px rgba(0,0,0,0.18)',
		saveBar: '0 4px 12px rgba(177,40,46,0.2)',
	},
	
	// Typography
	fontFamily: 'Saira, sans-serif',
	
	// Gradients
	gradients: {
		accentBar: 'linear-gradient(90deg, #B1282E 0%, #8B1F24 100%)',
		background: 'linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)',
		avatar: 'linear-gradient(135deg, #2d6a4f 0%, #1b4332 100%)',
	},
	
	// Section header styles
	sectionHeader: {
		fontWeight: 600,
		fontSize: '1.25rem',
		color: '#1a2332',
		marginBottom: 1,
		borderBottom: '2px solid #B1282E',
		paddingBottom: 1,
		display: 'inline-block',
	},
	
	// Paper section styles
	paperSection: {
		padding: 3,
		marginBottom: 2.5,
		boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
		backgroundColor: '#ffffff',
		border: '1px solid #e0e0e0',
	},
	
	// Accent bar styles
	accentBar: {
		width: '100%',
		height: '4px',
		background: 'linear-gradient(90deg, #B1282E 0%, #8B1F24 100%)',
	},
	
	// Section paper wrapper
	sectionPaper: {
		padding: 0,
		marginBottom: 2.5,
		boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
		overflow: 'hidden',
		border: '1px solid #e0e0e0',
	},
	
	// Section content box
	sectionContent: {
		paddingX: 3,
		paddingTop: 2,
		paddingBottom: 3,
		background: 'linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)',
	},
	
	// Label typography
	labelText: {
		fontFamily: 'Saira, sans-serif',
	},
	
	// Grid sizing
	grid: {
		labelColumn: { xs: 5, sm: 4, lg: 3 },
		contentColumn: { xs: 7, sm: 8, lg: 9 },
	},
	
	// Section heading container (icon + text with underline)
	sectionHeadingContainer: {
		display: 'inline-flex',
		alignItems: 'center',
		gap: 1,
		borderBottom: '2px solid #B1282E',
		paddingBottom: 1,
		marginBottom: 1,
	},
	
	// Section heading text
	sectionHeadingText: {
		fontWeight: 600,
		fontSize: '1.25rem',
		color: '#1a2332',
	},
	
	// Section heading icon
	sectionHeadingIcon: {
		color: '#6c757d',
		fontSize: '1.25rem',
	},
	
	// Save/Reset bar (sticky bottom)
	saveResetBar: {
		position: 'sticky',
		bottom: 0,
		zIndex: 1000,
		padding: 2.5,
		marginBottom: 0,
		marginTop: 2.5,
		boxShadow: '0 -4px 12px rgba(177,40,46,0.3)',
		backgroundColor: '#ffffff',
		border: '2px solid #B1282E',
	},
	
	// Save/Reset text styling
	saveResetText: {
		fontWeight: 600,
	},
	
	// Dialog styles
	dialog: {
		paper: {
			border: `2px solid #B1282E`,
			borderRadius: 2,
			boxShadow: '0 8px 24px rgba(177,40,46,0.2)',
			overflow: 'hidden',
		},
		title: {
			background: 'linear-gradient(90deg, #B1282E 0%, #8B1F24 100%)',
			color: '#ffffff',
			fontWeight: 600,
			fontFamily: 'Saira, sans-serif',
			padding: 1.25,
		},
		content: {
			paddingX: 3,
			paddingTop: 1.5,
			paddingBottom: 3,
			background: 'linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)',
		},
		actions: {
			padding: 2,
			backgroundColor: '#f8f9fa',
			borderTop: '1px solid #e0e0e0',
		},
	},
};

export default function UserProfile() {
	const [ searchParams ] = useSearchParams();
	const { userId } = useParams();
	const navigate = useNavigate();

	// Determine if we're in admin editing mode or self-editing mode
	const isAdminEditing = userId && /^\d+$/.test(userId) && Number(userId) > 0;

	const [toast, setToast] = useState({
		open: false,
		severity: 'info',
		message: '',
	});

	// Current logged-in user info (for permission checks)
	const [currentUser, setCurrentUser] = useState({
		id: 0,
		is_staff: false,
		is_superuser: false,
	});

	// The user being edited (either current user or another user)
	const [userInfo, setUserInfo] = useState({
		id: 0,
		email: '',
		password_reset: false,
		first_name: '',
		last_name: '',
		job_title: '',
		company: '',
		time_zone: '',
		metric: false,
		date_joined: '1990-01-01T00:00:00.000000Z',
		last_login: '1990-01-01T00:00:00.000000Z',
		pass_last_reset: '1990-01-01T00:00:00.000000Z',
		is_active: false,
		is_superuser: false,
		is_staff: false,
		is_self: false,
	});
	
	// Input fields for the form
	const [txtFirstName, setTxtFirstName] = useState('');
	const [txtLastName, setTxtLastName] = useState('');
	const [txtJobTitle, setTxtJobTitle] = useState('');
	const [txtCompany, setTxtCompany] = useState('');
	const [txtEmail, setTxtEmail] = useState('');
	const [txtTimeZone, setTxtTimeZone] = useState('');
	const [chkMetric, setChkMetric] = useState(false);
	
	// Permission checkboxes (admin/superuser only)
	const [chkPasswordReset, setChkPasswordReset] = useState(false);
	const [chkEnabled, setChkEnabled] = useState(true);
	const [chkAdmin, setChkAdmin] = useState(false);
	const [chkSuperuser, setChkSuperuser] = useState(false);

	// Keep track of changes to the form for saving purposes
	const [isChanged, setIsChanged] = useState(false);

	// Password reset dialog open state
	const [passDialogOpen, setPassDialogOpen] = useState(false);
	const [tempPassOpen, setTempPassOpen] = useState({ open: false, user: '', password: '' });

	// MFA status and dialog open states
	const [mfaStatusLoaded, setMFAStatusLoaded] = useState(false);
	const [mfaActive, setMFAActive] = useState(false);
	const [mfaSetupDialogOpen, setMFASetupDialogOpen] = useState(false);
	const [mfaDeactivateDialogOpen, setMFADeactivateDialogOpen] = useState(false);
	// const [mfaBackupDialogOpen, setMFABackupDialogOpen] = useState(false);
	
	// Form Validity
	const [emailValid, setEmailValid] = useState(false);
	const [firstNameValid, setFirstNameValid] = useState(false);
	const [lastNameValid, setLastNameValid] = useState(false);
	const [timeZoneValid, setTimeZoneValid] = useState(false);

	// State for the current password dialog
	const [currentPasswordDialogOpen, setCurrentPasswordDialogOpen] = useState(false);
	const [currentPassword, setCurrentPassword] = useState('');

	// Form Validity checks
	useEffect(() => { setEmailValid(isValidEmail(txtEmail)); }, [txtEmail]);
	
	useEffect(() => { setFirstNameValid(isValidName(txtFirstName)); }, [txtFirstName]);
	
	useEffect(() => { setLastNameValid(isValidName(txtLastName)); }, [txtLastName]);
	
	useEffect(() => { setTimeZoneValid(isValidTimeZone(txtTimeZone)); }, [txtTimeZone]);

	// Verify all form requirements are met
	const verifyRequirementsMet = emailValid && firstNameValid && lastNameValid && timeZoneValid;

	// Get the user info from the API
	useEffect(() => {
		if (isAdminEditing) {
			// Admin editing mode: fetch current user first, then target user
			getCurrentUserForAdmin();
		} else {
			// Self-editing mode: fetch current user once and use for both states
			getCurrentUserForSelf();
		}
	}, [userId, searchParams]);

	// Get the user's mfa status - currently only supports/checks for app-based MFA
	const getUserMFAStatus = useCallback(() => {
		const endpoint = isAdminEditing 
			? `/users/${userId}/mfa/`
			: '/auth/mfa/user-active-methods/';
		
		axiosInstance.get(endpoint)
			.then(response => {
				const mfaMethods = response.data.results;
				if (Array.isArray(mfaMethods) && mfaMethods.length > 0) {
					setMFAActive(mfaMethods.some(method => method.name === 'app'));
				} else {
					setMFAActive(false);
				}
				setMFAStatusLoaded(true);
			})
			.catch(error => {
				console.log('Error getting MFA status:', error);
				setMFAStatusLoaded(false);
			});
	}, [isAdminEditing, userId]);

	// Check the MFA activation status
	useEffect(() => {
		// If either dialog(s) open state changes and is false (they were closed), update the MFA status
		if (!mfaSetupDialogOpen || !mfaDeactivateDialogOpen) {
			getUserMFAStatus();
		}
	}, [mfaSetupDialogOpen, mfaDeactivateDialogOpen, getUserMFAStatus]);
	
	// Warn user before leaving with unsaved changes
	useEffect(() => {
		const handleBeforeUnload = (e) => {
			if (isChanged && verifyRequirementsMet) {
				e.preventDefault();
				e.returnValue = '';
			}
		};
		
		window.addEventListener('beforeunload', handleBeforeUnload);
		return () => window.removeEventListener('beforeunload', handleBeforeUnload);
	}, [isChanged, verifyRequirementsMet]);
	
	// Keep track of the change in form data
	useEffect(() => {
		if(userInfo.first_name !== txtFirstName ||
			userInfo.last_name !== txtLastName ||
			userInfo.job_title !== txtJobTitle ||
			userInfo.company !== txtCompany ||
			userInfo.email !== txtEmail ||
			userInfo.password_reset !== chkPasswordReset ||
			userInfo.is_active !== chkEnabled ||
			userInfo.is_staff !== chkAdmin ||
			userInfo.is_superuser !== chkSuperuser ||
			userInfo.time_zone !== txtTimeZone ||
			userInfo.metric !== chkMetric) {
			setIsChanged(true);
		} else {
			setIsChanged(false);
		}
	}, [userInfo, txtFirstName, txtLastName, txtJobTitle, txtCompany, txtEmail, chkPasswordReset, chkEnabled, chkAdmin, chkSuperuser, txtTimeZone, chkMetric]);

	// Get current user info for self-editing mode (single API call)
	function getCurrentUserForSelf() {
		axiosInstance.get('/users/')
			.then(response => {
				if (response.data.length === 0) { return; }

				const currentUserData = response.data;
				
				// Set both currentUser and userInfo with the same data
				setCurrentUser(currentUserData);
				setUserInfo(currentUserData);

				// Populate form fields
				populateFormFields(currentUserData);

				// Get MFA status for self-editing
				getUserMFAStatus();

				// Check for passreset query parameter
				if (searchParams.has('passreset')) {
					setPassDialogOpen(true);
				}
			})
			.catch(error => {
				console.error('Error fetching current user:', error);
				if (error.response && error.response.status === 404) {
					navigate('/404/', { replace: true });
				} else {
					navigate('/403/', { replace: true });
				}
			});
	}

	// Get current user for admin mode, then fetch target user
	function getCurrentUserForAdmin() {
		axiosInstance.get('/users/')
			.then(response => {
				if (response.data.length === 0) { return; }

				const currentUserData = response.data;
				setCurrentUser(currentUserData);

				// Check permissions
				if (!currentUserData.is_staff && !currentUserData.is_superuser) {
					navigate('/403/', { replace: true });
					return;
				}

				// Check if trying to edit own user via admin path
				if (parseInt(userId) === currentUserData.id) {
					// Redirect to regular profile page
					navigate('/profile', { replace: true });
					return;
				}

				// Now fetch the target user being edited
				getTargetUserInfo(currentUserData);
			})
			.catch(error => {
				console.error('Error fetching current user:', error);
				if (error.response && error.response.status === 404) {
					navigate('/404/', { replace: true });
				} else {
					navigate('/403/', { replace: true });
				}
			});
	}

	// Get the target user's info (when admin is editing another user)
	function getTargetUserInfo(currentUserData) {
		axiosInstance.get(`/users/${userId}/`)
			.then(response => {
				const targetUserData = response.data;

				if (!targetUserData || targetUserData.length === 0) {
					navigate('/404/', { replace: true });
					return;
				}

				// Check if trying to edit a superuser (only superusers can edit superusers)
				if (targetUserData.is_superuser && !currentUserData.is_superuser) {
					navigate('/403/', { replace: true });
					return;
				}

				setUserInfo(targetUserData);

				// Populate form fields
				populateFormFields(targetUserData);
			})
			.catch(error => {
				console.error('Error fetching target user info:', error);
				if (error.response && error.response.status === 404) {
					navigate('/404/', { replace: true });
				} else {
					navigate('/403/', { replace: true });
				}
			});
	}

	// Helper function to populate form fields from user data
	function populateFormFields(userData) {
		setTxtFirstName(userData.first_name);
		setTxtLastName(userData.last_name);
		setTxtJobTitle(userData.job_title);
		setTxtCompany(userData.company);
		setTxtEmail(userData.email);
		setChkPasswordReset(userData.password_reset);
		setChkEnabled(userData.is_active);
		setChkAdmin(userData.is_staff);
		setChkSuperuser(userData.is_superuser);
		setTxtTimeZone(userData.time_zone);
		setChkMetric(userData.metric);
	}

	// Update the user's profile info
	function updateUserInfo(password = '', onSuccess = () => {}, onError = () => {}) {
		// Prepare the payload for the API request
		const payload = {
			auth: {
				current_password: password
			},
			data: {
				first_name: txtFirstName === '' ? userInfo.first_name : txtFirstName,
				last_name: txtLastName === '' ? userInfo.last_name : txtLastName,
				job_title: txtJobTitle,
				company: txtCompany,
				email: !emailValid ? userInfo.email : txtEmail,
				time_zone: !timeZoneValid ? userInfo.time_zone : txtTimeZone,
				metric: chkMetric
			}
		};

		// Add admin-only fields if in admin editing mode
		if (isAdminEditing) {
			payload.data.password_reset = chkPasswordReset;
			payload.data.is_active = chkEnabled;
			payload.data.is_staff = chkAdmin;
			payload.data.is_superuser = chkSuperuser;
		}

		// Call the User Update endpoint to update the database
		axiosInstance.put(`/users/change/${userInfo.id}/`, payload)
			.then(response => {
				setToast({ severity: 'success', message: 'User info updated successfully.', open: true });
				
				// Refresh user data
				if (isAdminEditing) {
					getTargetUserInfo(currentUser);
				} else {
					getCurrentUserForSelf();
				}
				
				setIsChanged(false); // Clear the save changes bar
				onSuccess(); // Success callback
			})
			.catch(error => {
				if (error.response) {
					const { status, data } = error.response;

					// Check for specific error codes and messages
					if (status === 403 && data.error === 'Current password is incorrect.') {
						setToast({ severity: 'error', message: data.error, open: true });
					} else if (status === 400 && data.error === 'Current password is required to update the email address.') {
						setToast({ severity: 'error', message: data.error, open: true });
					} else {
						setToast({ severity: 'error', message: 'Error updating user info. Please try again.', open: true });
					}
				} else {
					// Handle other errors (e.g., network issues)
					setToast({ severity: 'error', message: 'An unexpected error occurred. Please try again.', open: true });
				}
				onError(); // Error callback
			});

		// Save the user's timezone and measurement units preferences in local storage
		UserPrefsManager.set('userTimeZone', (!timeZoneValid ? userInfo.time_zone : txtTimeZone));
		UserPrefsManager.set('userMeasUnits', (chkMetric ? 'metric' : 'imperial'));
	}

	// Handle form changes save
	function handleSave() {
		if (txtEmail !== userInfo.email) {
			// Open the current password dialog if the email is being updated
			setCurrentPasswordDialogOpen(true);
		} else {
			// Proceed with saving if the email is not being updated
			updateUserInfo();
		}
	}

	// Function to handle saving after entering the current password
	function handleSaveWithPassword() {
		updateUserInfo(
			currentPassword,
			() => {
				// On success, close the dialog
				setCurrentPassword('');
				setCurrentPasswordDialogOpen(false);
			},
			() => {
				// On error, keep the dialog open
				setCurrentPassword(''); // Optionally clear the password field
			}
		);
	}

	// Handle form changes reset
	function handleReset() {
		setTxtFirstName(userInfo.first_name);
		setTxtLastName(userInfo.last_name);
		setTxtJobTitle(userInfo.job_title);
		setTxtCompany(userInfo.company);
		setTxtEmail(userInfo.email);
		setChkPasswordReset(userInfo.password_reset);
		setChkEnabled(userInfo.is_active);
		setChkAdmin(userInfo.is_staff);
		setChkSuperuser(userInfo.is_superuser);
		setTxtTimeZone(userInfo.time_zone);
		setChkMetric(userInfo.metric);

		setIsChanged(false);
	}
	
	// Handle MFA setup dialog open
	function handleMFAStatusChange() {
		if (!mfaActive) {
			setMFASetupDialogOpen(true);
		} else {
			setMFADeactivateDialogOpen(true);
		}
	}

	// Handle MFA backup codes dialog open
	// function handleMFABackupOpen() {
	// 	setMFABackupDialogOpen(true);
	// }

	// Reset user password with a password reset email. If that fails, fall back to resetting the password to a temporary password
	function resetUserPassword() {
			axiosInstance.post(`password_reset/`, {
					email: userInfo.email,
				})
				.then((res) => {
					setToast({ severity: 'success', message: 'A password reset email has been sent to: ' + userInfo.email, open: true });
				})
				.catch((error) => {
					setToast({ severity: 'error', message: 'Error sending password reset email: ' + error, open: true });
					console.log(`Error sending password reset email to ${userInfo.email}: ${error}`);

					// If the email sending fails, reset the password to a temporary password and display to the admin
					resetUserPasswordTemp();
				});
		}

	// Reset user password to temp password
	function resetUserPasswordTemp() {
		axiosInstance.post(`/users/passreset/`, { id: userInfo.id })
			.then(response => {
				let userName = userInfo.first_name + ' ' + userInfo.last_name;
				setTempPassOpen({ open: true, user: userName, password: response.data.new_password });
			})
			.catch(error => {
				setToast({ severity: 'error', message: `Error resetting user account ${userInfo.email}'s password: ${error}`, open: true });
				console.log(`Error resetting user account ${userInfo.email}'s password: ${error}`);
			});
	}

	// Handle password reset button click
	const handlePasswordResetClick = () => {
		if (isAdminEditing) {
			if (window.confirm(`Are you sure you want to reset the password for this user?`)) {
				resetUserPassword();
			}
		} else {
			setPassDialogOpen(true);
		}
	};

	// Handle the Superuser switch - If superuser is enabled, set admin to true
	const handleIsSuperuserChange = () => {
		if (!chkSuperuser && window.confirm('Are you sure you want to make this user a Superuser?')) {
			setChkAdmin(true);
			setChkSuperuser(true);
		} else {
			setChkSuperuser(false);
		}
	};

	// MFA Section JSX
	const mfaSection = (
		<Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap'}}>
			{ !mfaStatusLoaded ? (
				<Box sx={{ margin: 1 }}>
					<Typography sx={{ fontFamily: STYLES.fontFamily, fontWeight: 600 }}>Loading...</Typography>
				</Box>
			) : (
				<>
					<Box sx={{ margin: 1 }}>
						<Typography sx={{ fontFamily: STYLES.fontFamily, fontWeight: 600 }}>{mfaActive ? 'Active' : 'Inactive'}</Typography>
					</Box>
					{ (!isAdminEditing || (isAdminEditing && mfaActive)) && (
						<Box sx={{ margin: 1 }}>
							<Tooltip title={ (mfaActive ? 'Deactivate' : 'Setup') + ' multifactor authentication' }>
								<Button onClick={() => handleMFAStatusChange()} variant="contained" color="primary" >
									{mfaActive ? 'Deactivate' : 'Setup'}
								</Button>
							</Tooltip>
						</Box>
					)}
				</>
			) }
		</Box>
	);

	return (
		<Paper
			padding='true'
			sx={{
				overflow: 'auto',
				boxShadow: '0',
				height: '100%',
				display: 'flex',
				flexDirection: 'column',
				paddingX: '15px',
				backgroundColor: STYLES.colors.backgroundPage,
			}}
		>
			{/* Page Title */}
			<Typography
				variant='h5'
				fontFamily={STYLES.fontFamily}
				fontWeight='600'
				color={STYLES.colors.textDark}
				align='center'
				paddingTop='5px'
				paddingBottom={ userInfo.is_superuser || userInfo.is_staff ? '0px' : '15px'}
			>
				{ isAdminEditing ? `Editing ${userInfo.first_name} ${userInfo.last_name}'s User Account` : 'My User Account' }
			</Typography>
			{ ( userInfo.is_superuser || userInfo.is_staff ) &&
				<Typography
					fontFamily={STYLES.fontFamily}
					fontWeight='400'
					color={STYLES.colors.textDark}
					align='center'
					paddingBottom='15px'
				>
					{userInfo.is_superuser ? 'Superuser' : 'Administrator'}
				</Typography>
			}
			
			<Toast
				severity={toast.severity}
				message={toast.message}
				toastOpen={toast.open}
				setToastOpen={() => setToast({ ...toast, open: false })}
			/>
			
			{/* Centered container for all content */}
			<Box sx={{ maxWidth: STYLES.spacing.containerMaxWidth, marginX: 'auto', width: '100%' }}>
				<Grid container spacing={2}>
					{/* User Avatar Column */}
					<Grid item xs={12} md={3} lg={2} container justifyContent="center">
						<Box width={200} height={275}>
							<Paper 
								sx={{ 
									padding: 0,
									height: '100%', 
									display: 'flex', 
									flexDirection: 'column', 
									alignItems: 'center', 
									justifyContent: 'center',
									boxShadow: STYLES.shadows.avatarCard,
									overflow: 'hidden',
									transition: 'transform 0.2s, box-shadow 0.2s',
									'&:hover': {
										transform: 'translateY(-3px)',
										boxShadow: STYLES.shadows.avatarCardHover
									}
								}}
							>
								{/* Top accent bar */}
								<Box sx={STYLES.accentBar} />
								<Box 
									sx={{ 
										padding: 2.5,
										display: 'flex',
										flexDirection: 'column',
										alignItems: 'center',
										justifyContent: 'center',
										flex: 1,
										background: STYLES.gradients.background
									}}
								>
									<Avatar 
										sx={{ 
											width: 110, 
											height: 110, 
											background: STYLES.gradients.avatar,
											boxShadow: STYLES.shadows.avatar,
											border: '3px solid white'
										}}
									>
										<Typography fontFamily={STYLES.fontFamily} variant="h2" sx={{ fontWeight: 700, color: 'white' }}>
											{`${(userInfo.first_name[0] || '')}${(userInfo.last_name[0] || '')}`.toUpperCase()}
										</Typography>
									</Avatar>
									<Typography 
										fontFamily={STYLES.fontFamily} 
										fontWeight={700} 
										variant="h5" 
										align="center" 
										sx={{ pt: 2, pb: 0.25, color: STYLES.colors.textPrimary }}
									>
										{userInfo.first_name} {userInfo.last_name}
									</Typography>
									<Typography 
										fontFamily={STYLES.fontFamily} 
										variant="body2" 
										align="center" 
										sx={{ py: 0.25, color: STYLES.colors.textSecondary, fontStyle: 'italic' }}
									>
										{userInfo.job_title}
									</Typography>
									<Typography 
										fontFamily={STYLES.fontFamily} 
										fontWeight={600} 
										variant="body2" 
										align="center" 
										sx={{ color: STYLES.colors.textTertiary }}
									>
										{userInfo.company}
									</Typography>
								</Box>
							</Paper>
						</Box>
					</Grid>
					
					{/* Main Content Column */}
					<Grid item xs={12} md={9} lg={10}>
						{/* User Info Section */}
						<Paper sx={STYLES.sectionPaper}>
							{/* Top accent bar */}
							<Box sx={STYLES.accentBar} />
							<Box sx={STYLES.sectionContent}>
								<Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
									<Grid container spacing={2}>
										<Grid item xs={12}>
											<Box sx={STYLES.sectionHeadingContainer}>
												<PersonIcon sx={STYLES.sectionHeadingIcon} />
												<Typography variant="h5" sx={STYLES.sectionHeadingText}>
													User Info
												</Typography>
											</Box>
										</Grid>
										<Grid item xs={6}>
											<TextField
												label="First Name"
												value={txtFirstName}
												error={!firstNameValid}
												helperText={!firstNameValid ? 'Please enter a valid first name' : ''}
												onChange={(e) => setTxtFirstName(e.target.value)}
												size='small'
												fullWidth
												inputProps={{ maxLength: 50 }}
											/>
										</Grid>
										<Grid item xs={6}>
											<TextField
												label="Last Name"
												value={txtLastName}
												error={!lastNameValid}
												helperText={!lastNameValid ? 'Please enter a valid last name' : ''}
												onChange={(e) => setTxtLastName(e.target.value)}
												size='small'
												fullWidth
												inputProps={{ maxLength: 50 }}
											/>
										</Grid>
										<Grid item xs={12}>
											<TextField
												label="Job Title"
												value={txtJobTitle}
												onChange={(e) => setTxtJobTitle(e.target.value)}
												size='small'
												fullWidth
												inputProps={{ maxLength: 64 }}
											/>
										</Grid>
										<Grid item xs={12}>
											<TextField
												label="Company Name"
												value={txtCompany}
												onChange={(e) => setTxtCompany(e.target.value)}
												size='small'
												fullWidth
												inputProps={{ maxLength: 64 }}
											/>
										</Grid>
									</Grid>
								</Box>
							</Box>
						</Paper>

						{/* Authentication Section */}
						<TempPassDialog
							tempPassOpen={tempPassOpen.open}
							setTempPassOpen={() => setTempPassOpen({ open: false, user: '', password: '' })}
							username={tempPassOpen.user}
							tempPassword={tempPassOpen.password}
						/>
						<Dialog
							open={currentPasswordDialogOpen}
							onClose={() => setCurrentPasswordDialogOpen(false)}
							maxWidth="sm"
							fullWidth
							sx={{
								'.MuiDialog-paper': STYLES.dialog.paper
							}}
						>
							<DialogTitle sx={STYLES.dialog.title}>
								<Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
									<LockIcon sx={{ fontSize: '1.5rem' }} />
									Confirm Email Change
								</Box>
							</DialogTitle>
							<DialogContent sx={STYLES.dialog.content}>
								<Typography variant="body1" sx={{ marginTop: 2.5, marginBottom: 1.5, color: STYLES.colors.textSecondary }}>
									To update {isAdminEditing ? 'this user\'s' : 'your'} email address, please enter your current password.
								</Typography>
								<TextField
									autoFocus
									id="current_password"
									label="Current Password"
									type="password"
									value={currentPassword}
									onChange={(e) => setCurrentPassword(e.target.value)}
									inputProps={{ maxLength: 32 }}
									fullWidth
									margin="dense"
									sx={{
										'& .MuiOutlinedInput-root': {
											'&:hover fieldset': {
												borderColor: STYLES.colors.brandRed,
											},
											'&.Mui-focused fieldset': {
												borderColor: STYLES.colors.brandRed,
											},
										},
										'& .MuiInputLabel-root.Mui-focused': {
											color: STYLES.colors.brandRed,
										},
									}}
								/>
							</DialogContent>
							<DialogActions sx={STYLES.dialog.actions}>
								<Button 
									onClick={() => setCurrentPasswordDialogOpen(false)}
									variant="outlined"
									sx={{ 
										color: STYLES.colors.textSecondary,
										borderColor: STYLES.colors.borderLight,
										'&:hover': {
											borderColor: STYLES.colors.textSecondary,
											backgroundColor: STYLES.colors.backgroundLight,
										}
									}}
								>
									Cancel
								</Button>
								<Button 
									onClick={handleSaveWithPassword} 
									variant="contained"
									sx={{
										backgroundColor: STYLES.colors.brandRed,
										'&:hover': {
											backgroundColor: STYLES.colors.brandRedDark,
										},
									}}
								>
									Save
								</Button>
							</DialogActions>
						</Dialog>
						<Paper sx={STYLES.sectionPaper}>
							{/* Top accent bar */}
							<Box sx={STYLES.accentBar} />
							<Box sx={STYLES.sectionContent}>
								<Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
									<Grid container spacing={2}>
										<Grid item xs={12}>
											<Box sx={STYLES.sectionHeadingContainer}>
												<LockIcon sx={STYLES.sectionHeadingIcon} />
												<Typography variant="h5" sx={STYLES.sectionHeadingText}>
													Authentication
												</Typography>
											</Box>
										</Grid>
										<Grid item xs={12}>
											<TextField
												label="Email"
												value={txtEmail}
												error={!emailValid}
												helperText={!emailValid ? 'Please enter a valid email address' : ''}
												onChange={(e) => setTxtEmail(e.target.value)}
												size='small'
												fullWidth
												inputProps={{ maxLength: 64 }}
											/>
										</Grid>
										<Grid item {...STYLES.grid.labelColumn}>
											<Box display="flex" alignItems="center" height="100%">
												<Typography sx={STYLES.labelText}>Password:</Typography>
											</Box>
										</Grid>
										<Grid item {...STYLES.grid.contentColumn}>
											<Tooltip title={`Reset ${isAdminEditing ? 'this user\'s' : 'your'} password`}>
												<Button onClick={handlePasswordResetClick} variant="contained" color="primary">Reset</Button>
											</Tooltip>
										</Grid>
										{isAdminEditing && (
											<>
												<Grid item {...STYLES.grid.labelColumn}>
													<Box display="flex" alignItems="center" height="100%">
														<Typography sx={STYLES.labelText}>Reset Pass Next Login:</Typography>
													</Box>
												</Grid>
												<Grid item {...STYLES.grid.contentColumn}>
													<DataViewSwitch
														checked={chkPasswordReset}
														onChange={() => setChkPasswordReset(!chkPasswordReset)}
														leftText=''
														rightText=''
														leftSwitchOverride='Require user to reset password on next login'
														rightSwitchOverride='Do not require password reset on next login'
													/>
												</Grid>
											</>
										)}
										<Grid item {...STYLES.grid.labelColumn}>
											<Box display="flex" alignItems="center" height="100%">
												<Typography sx={STYLES.labelText}>Multifactor Authentication:</Typography>
											</Box>
										</Grid>
										<Grid item {...STYLES.grid.contentColumn}>
											{mfaSection}
										</Grid>
									</Grid>
									<PasswordResetDialog
										emailAddress={userInfo.email}
										dialogOpen={passDialogOpen}
										setDialogOpen={setPassDialogOpen}
									/>
									<MFASetupDialog
										dialogOpen={mfaSetupDialogOpen}
										setDialogOpen={setMFASetupDialogOpen}
									/>
									<DeactivateMFADialog
										dialogOpen={mfaDeactivateDialogOpen}
										setDialogOpen={setMFADeactivateDialogOpen}
										{...(isAdminEditing && { userId })}
									/>
								</Box>
							</Box>
						</Paper>

						{/* Admin-Only Permissions Section */}
						{isAdminEditing && (
							<Paper sx={STYLES.sectionPaper}>
								{/* Top accent bar */}
								<Box sx={STYLES.accentBar} />
								<Box sx={STYLES.sectionContent}>
									<Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
										<Grid container spacing={2}>
											<Grid item xs={12}>
												<Box sx={STYLES.sectionHeadingContainer}>
													<AdminPanelSettingsIcon sx={STYLES.sectionHeadingIcon} />
													<Typography variant="h5" sx={STYLES.sectionHeadingText}>
														Permissions
													</Typography>
												</Box>
											</Grid>
											<Grid item {...STYLES.grid.labelColumn}>
												<Box display="flex" alignItems="center" height="100%">
													<Typography sx={STYLES.labelText}>Account Enabled:</Typography>
												</Box>
											</Grid>
											<Grid item {...STYLES.grid.contentColumn}>
												<DataViewSwitch
													checked={chkEnabled}
													onChange={() => setChkEnabled(!chkEnabled)}
													leftText=''
													rightText=''
													leftSwitchOverride='Enable User Account (Allow access to the site)'
													rightSwitchOverride='Disable User Account (Disallow access to the site - user cannot login)'
												/>
											</Grid>
											<Grid item {...STYLES.grid.labelColumn}>
												<Box display="flex" alignItems="center" height="100%">
													<Typography sx={STYLES.labelText}>Administrator:</Typography>
												</Box>
											</Grid>
											<Grid item {...STYLES.grid.contentColumn}>
												<DataViewSwitch
													checked={chkAdmin}
													onChange={() => setChkAdmin(!chkAdmin)}
													leftText=''
													rightText=''
													leftSwitchOverride='Make the user an Administrator (Administrators can manage User accounts associated with the Locations assigned to them)'
													rightSwitchOverride='Remove Administrator privileges from the user'
													disabled={chkSuperuser}
													disabledText='Superusers have all permissions, so this setting cannot be changed while Superuser is enabled'
												/>
											</Grid>
											<Grid item {...STYLES.grid.labelColumn}>
												<Box display="flex" alignItems="center" height="100%">
													<Typography sx={STYLES.labelText}>Superuser:</Typography>
												</Box>
											</Grid>
											<Grid item {...STYLES.grid.contentColumn}>
												<DataViewSwitch
													checked={chkSuperuser}
													onChange={handleIsSuperuserChange}
													leftText=''
													rightText=''
													leftSwitchOverride='Make the user a Superuser (Superusers can access/change anything on the site)'
													rightSwitchOverride='Remove Superuser privileges from the user'
												/>
											</Grid>
										</Grid>
									</Box>
								</Box>
							</Paper>
						)}

						{/* Preferences Section */}
						<Paper sx={STYLES.sectionPaper}>
							{/* Top accent bar */}
							<Box sx={STYLES.accentBar} />
							<Box sx={STYLES.sectionContent}>
								<Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
									<Grid container spacing={2}>
										<Grid item xs={12}>
											<Box sx={STYLES.sectionHeadingContainer}>
												<SettingsIcon sx={STYLES.sectionHeadingIcon} />
												<Typography variant="h5" sx={STYLES.sectionHeadingText}>
													Preferences
												</Typography>
											</Box>
										</Grid>
										<Grid item {...STYLES.grid.labelColumn}>
											<Box display="flex" alignItems="center" height="100%">
												<Typography sx={STYLES.labelText}>Time Zone:</Typography>
											</Box>
										</Grid>
										<Grid item {...STYLES.grid.contentColumn}>
											<Tooltip title='Choose the timezone that all dates on the site will be shown in'>
												<Select
													value={txtTimeZone}
													error={!timeZoneValid}
													onChange={(e) => setTxtTimeZone(e.target.value)}
													size='small'
													style={{ width: '100%' }}
												>
													{TIME_ZONES.map((option) => (
														<MenuItem key={option} value={option}>
															{option}
														</MenuItem>
													))}
												</Select>
											</Tooltip>
										</Grid>
										<Grid item {...STYLES.grid.labelColumn}>
											<Box display="flex" alignItems="center" height="100%">
												<Typography sx={STYLES.labelText}>Units:</Typography>
											</Box>
										</Grid>
										<Grid item {...STYLES.grid.contentColumn}>
											<DataViewSwitch
												checked={chkMetric}
												onChange={() => setChkMetric(!chkMetric)}
												leftText='Imperial'
												rightText='Metric'
												leftSwitchOverride='Switch to Metric Units'
												rightSwitchOverride='Switch to Imperial Units'
											/>
										</Grid>
									</Grid>
								</Box>
							</Box>
						</Paper>

						{/* Account Activity Section */}
						<Paper sx={STYLES.sectionPaper}>
							{/* Top accent bar */}
							<Box sx={STYLES.accentBar} />
							<Box sx={STYLES.sectionContent}>
								<Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
									<Grid container spacing={2}>
										<Grid item xs={12}>
											<Box sx={STYLES.sectionHeadingContainer}>
												<HistoryIcon sx={STYLES.sectionHeadingIcon} />
												<Typography variant="h5" sx={STYLES.sectionHeadingText}>
													Account Activity
												</Typography>
											</Box>
										</Grid>
										<Grid item {...STYLES.grid.labelColumn}>
											<Box display="flex" alignItems="center" height="100%">
												<Typography sx={STYLES.labelText}>Account Created:</Typography>
											</Box>
										</Grid>
										<Grid item {...STYLES.grid.contentColumn}>
											<Box display="flex" alignItems="center" height="100%">
												<Typography sx={STYLES.labelText}>{formatDateTime(userInfo.date_joined)}</Typography>
											</Box>
										</Grid>
										<Grid item {...STYLES.grid.labelColumn}>
											<Box display="flex" alignItems="center" height="100%">
												<Typography sx={STYLES.labelText}>Last Activity:</Typography>
											</Box>
										</Grid>
										<Grid item {...STYLES.grid.contentColumn}>
											<Box display="flex" alignItems="center" height="100%">
												<Typography sx={STYLES.labelText}>{formatDateTime(userInfo.last_login)}</Typography>
											</Box>
										</Grid>
										<Grid item {...STYLES.grid.labelColumn}>
											<Box display="flex" alignItems="center" height="100%">
												<Typography sx={STYLES.labelText}>Password Last Changed:</Typography>
											</Box>
										</Grid>
										<Grid item {...STYLES.grid.contentColumn}>
											<Box display="flex" alignItems="center" height="100%">
												<Typography sx={STYLES.labelText}>{formatDateTime(userInfo.pass_last_reset)}</Typography>
											</Box>
										</Grid>
									</Grid>
								</Box>
							</Box>
						</Paper>

						{/* Save/Reset Section - Sticky at Bottom when there are unsaved changes */}
						{isChanged && verifyRequirementsMet && (
							<Paper sx={STYLES.saveResetBar}>
								<Box display="flex" justifyContent="space-between" alignItems="center">
									<Typography variant="body1" sx={STYLES.saveResetText}>You have unsaved changes. Would you like to save?</Typography>
									<Box>
										<Button variant="contained" onClick={handleReset} style={{ marginRight: 16 }}>Reset</Button>
										<Button variant="contained" color="primary" onClick={handleSave}>Save</Button>
									</Box>
								</Box>
							</Paper>
						)}
					</Grid>
				</Grid>
			</Box>
		</Paper>
	);
}